import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, repeatsAClass, treatsSeveral } from '../skillUsage';

export const streamingTextResponseSkill: CodeoSkill = {
    name: SKILL_NAMES.streamingTextResponse,
    kind: 'codeo',
    description:
        'Stream a block of text in word by word with a soft leading edge, the way a model writes ' +
        '— for showing something being generated rather than typed by a person.',
    checkLooksFor:
        'text in repeated spans, a blur in the tween, and one treatment reaching several spans',
    documentUsesSkill: check(
        (script, markup) =>
            repeatsAClass(markup, 'span') && /blur\(/.test(script) && treatsSeveral(script),
    ),
    instructions: `# Streaming Text Response

Words arrive in small groups with a soft blur on the leading edge, faster than anyone types and without a caret. It is the visual signature of generation, and it reads as a machine producing an answer — distinct from \`${SKILL_NAMES.typewriterWithCaret}\`, which reads as a person composing one.

Use it for an AI feature, a summary appearing, a draft being written. Do not use it for ordinary copy: streaming implies the text is being produced *now*, which is a claim.

## Markup

Split into word spans at author time so each can be revealed independently. Inline-block is required for the per-word blur and rise to apply.

\`\`\`html
<div id="response" style="position: absolute; left: 320px; top: 340px; width: 1280px; font-family: var(--l5-font-0), sans-serif; font-size: 56px; font-weight: 500; line-height: 1.42; color: var(--l5-color-0, #101820);">
  <span class="word" style="display: inline-block;">Revenue</span>
  <span class="word" style="display: inline-block;">grew</span>
  <span class="word" style="display: inline-block;">fastest</span>
  <span class="word" style="display: inline-block;">in</span>
  <span class="word" style="display: inline-block;">the</span>
  <span class="word" style="display: inline-block;">regions</span>
  <span class="word" style="display: inline-block;">you</span>
  <span class="word" style="display: inline-block;">entered</span>
  <span class="word" style="display: inline-block;">last</span>
  <span class="word" style="display: inline-block;">year.</span>
</div>
\`\`\`

## Timeline code

A short per-word stagger with a blur that resolves is the whole effect. The blur is what separates streaming from a plain word cascade.

\`\`\`js
const tl = gsap.timeline({ paused: true });

gsap.set('.word', { opacity: 0, y: 8, filter: 'blur(6px)' });

tl.to('.word', {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    duration: 0.34,
    ease: 'power2.out',
    stagger: 0.055,
}, 0.4);

window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Rate**: 0.05–0.07s per word reads as fast generation. Slower reads as reading aloud; faster and the stagger stops being perceptible.
- **Uneven cadence**: real streaming is bursty. A \`stagger\` of \`{ each: 0.055, from: 'start' }\` is even; hand-offsetting a couple of words by an extra 0.12s reads noticeably more authentic.
- **Line by line**: for a longer block, stagger whole lines instead of words — cheaper to rasterize and easier to read at pace.
- **Leading indicator**: a small pulsing dot at the end that disappears when the stream completes reads as "still generating". One dot, and it must stop.
- **Prompt above**: pair with a typed question — see \`${SKILL_NAMES.typewriterWithCaret}\` — and the pair tells the whole interaction in one scene.
- **Highlight on completion**: once streamed, marking one phrase is a strong second phase — see \`${SKILL_NAMES.markerEmphasis}\`.

## Pitfalls

- Word spans must be \`display: inline-block\` or neither the rise nor the blur applies.
- Splitting on whitespace at author time preserves the spaces between spans; splitting a line into spans with no whitespace between them runs the words together.
- Keep the block to about 12 words. A streamed paragraph is a wall of text arriving slowly, which is the opposite of a motion graphic.
- Blur on many elements at once is the expensive part here. Line-level staggering is markedly cheaper than word-level for long blocks.
- The stream must *finish* well before the scene ends, with time to read what it produced.
- Don't stream and also move the container. The words arriving is the motion.
- \`gsap.set\` establishes the from-state at build time, so the stagger must not add \`immediateRender: false\`.`,
};
